/**
 * Utilities for Order Form (passwords, slugs, hostname)
 *
 * These helpers are UI-framework agnostic and safe to unit test.
 */

// Slug sanitation
/**
 * @param {string} v Raw string to transform to a URL/file-system safe slug
 * @returns {string} sanitized lowercase slug (a-z0-9-)
 */
export function sanitizeSlug(v) {
  return (v || "")
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

// Password Generator (Geist-compliant, 22+ chars)
/**
 * Create a secure password that satisfies product rules:
 *  - length >= 22
 *  - at least 1 uppercase (not at the first/last positions)
 *  - at least 1 digit
 *  - cannot end with digit/special
 * @returns {string}
 */
export function generatePassword() {
  const totalLen = 22;
  const lower = "abcdefghjkmnpqrstuvwxyz";
  const upper = "ABCDEFGHJKMNPQRSTUVWXYZ";
  const digits = "23456789";
  const specials = "!@#$%&*_";
  const pick = (s) => s[Math.floor(Math.random() * s.length)];

  const arr = Array(totalLen).fill(null);
  // ensure first/last are lowercase
  arr[0] = pick(lower);
  arr[totalLen - 1] = pick(lower);
  // positions for mandatory characters (not edges)
  const upperPos = 1 + Math.floor(Math.random() * (totalLen - 2));
  const digitPos = 1 + Math.floor(Math.random() * (totalLen - 2));
  const specialPos = 1 + Math.floor(Math.random() * (totalLen - 2));
  arr[upperPos] = pick(upper);
  arr[digitPos] = pick(digits);
  arr[specialPos] = pick(specials);

  for (let i = 0; i < totalLen; i++) {
    if (!arr[i]) arr[i] = pick(lower + digits);
  }
  // Last char cannot be digit/special or uppercase
  if (/[^a-z]$/.test(arr.join(""))) arr[totalLen - 1] = pick(lower);
  return arr.join("");
}

// Random VPS Name Generator
/**
 * Generate a random VPS name in format: "my-cloud-vps-xxx-xxx" (3 letters each)
 * @returns {string} Random name like "my-cloud-vps-xyl-mnp"
 */
export function generateRandomVpsName() {
  const letters = "abcdefghijklmnopqrstuvwxyz";
  const pickRandom = () => {
    let result = "";
    for (let i = 0; i < 3; i++) {
      result += letters[Math.floor(Math.random() * letters.length)];
    }
    return result;
  };
  return `my-cloud-vps-${pickRandom()}-${pickRandom()}`;
}

// Random Space Name Generator
/**
 * Generate a random Space name in format: "my-space-xxx-xxx" (3 letters each)
 * @returns {string} Random name like "my-space-xyl-mnp"
 */
export function generateRandomSpaceName() {
  const letters = "abcdefghijklmnopqrstuvwxyz";
  const pickRandom = () => {
    let result = "";
    for (let i = 0; i < 3; i++) {
      result += letters[Math.floor(Math.random() * letters.length)];
    }
    return result;
  };
  return `my-space-${pickRandom()}-${pickRandom()}`;
}

// Hostname computation
// eslint-disable-next-line no-unused-vars
const defaultSlug = (val, fallback) => sanitizeSlug(val) || fallback;

/**
 * Compute a default hostname based on the current selections.
 * @param {{formData: any, applications: Array<any>, distributions: Array<any>}} params
 * @returns {string} hostname like "ubuntu-s-1vcpu-512mb-10gb-sfo2-1"
 */
export function computeDefaultHostname({
  formData,
  applications,
  distributions,
}) {
  let imagePart = "image";
  const app = applications.find((a) => a.slug === formData.image);
  if (app) imagePart = app.slug;
  else {
    const dist = distributions.find((d) => d.slug === formData.image);
    if (dist)
      imagePart = dist.slug || dist.distribution || dist.name || "image";
  }
  imagePart = sanitizeSlug(imagePart);
  let sizePart = sanitizeSlug(formData.size || "s-1vcpu-512mb-10gb");
  let regionPart = sanitizeSlug(formData.region || "sfo2");
  return `${imagePart}-${sizePart}-${regionPart}-1`;
}
